import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    InputAdornment,
    IconButton,
    Avatar,
    Fade,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { Visibility, VisibilityOff, CloudUpload, Delete, CheckCircle, Warning, DeleteForever, Help as HelpIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { getProfile, updateProfile, checkUsernameAvailability, deleteBranchDataForTesting } from "../../api/api";
import { getCurrentBranchId, getCurrentUser, clearAuthData } from "../../utils/auth";
import { getFrontendUrl, getStorageUrl } from "../../config/config";
import Swal from "sweetalert2";
import DefaultLogo from "../../assests/logo.png";
import OnboardingTutorialOverlay from "../../components/OnboardingTutorialOverlay";

const COUNTRY_PHONE_PREFIXES = {
    NO: { country: "Norway", phone: "+47" },
    SE: { country: "Sweden", phone: "+46" },
    DK: { country: "Denmark", phone: "+45" },
    FI: { country: "Finland", phone: "+358" },
    IS: { country: "Iceland", phone: "+354" },
    US: { country: "United States", phone: "+1" },
    GB: { country: "United Kingdom", phone: "+44" },
    DE: { country: "Germany", phone: "+49" },
    FR: { country: "France", phone: "+33" },
    ES: { country: "Spain", phone: "+34" },
    IT: { country: "Italy", phone: "+39" },
    NL: { country: "Netherlands", phone: "+31" },
    BE: { country: "Belgium", phone: "+32" },
    AT: { country: "Austria", phone: "+43" },
    CH: { country: "Switzerland", phone: "+41" },
    PL: { country: "Poland", phone: "+48" },
    PT: { country: "Portugal", phone: "+351" },
    IE: { country: "Ireland", phone: "+353" },
    CA: { country: "Canada", phone: "+1" },
    AU: { country: "Australia", phone: "+61" },
    NZ: { country: "New Zealand", phone: "+64" },
    IN: { country: "India", phone: "+91" },
    JP: { country: "Japan", phone: "+81" },
    KR: { country: "South Korea", phone: "+82" },
    CN: { country: "China", phone: "+86" },
    BR: { country: "Brazil", phone: "+55" },
    MX: { country: "Mexico", phone: "+52" },
    ZA: { country: "South Africa", phone: "+27" },
    AE: { country: "United Arab Emirates", phone: "+971" },
    SG: { country: "Singapore", phone: "+65" },
    PH: { country: "Philippines", phone: "+63" },
    TH: { country: "Thailand", phone: "+66" },
    MY: { country: "Malaysia", phone: "+60" },
    ID: { country: "Indonesia", phone: "+62" },
    PK: { country: "Pakistan", phone: "+92" },
    BD: { country: "Bangladesh", phone: "+880" },
    NG: { country: "Nigeria", phone: "+234" },
    EG: { country: "Egypt", phone: "+20" },
    TR: { country: "Turkey", phone: "+90" },
    RU: { country: "Russia", phone: "+7" },
    UA: { country: "Ukraine", phone: "+380" },
    RO: { country: "Romania", phone: "+40" },
    CZ: { country: "Czech Republic", phone: "+420" },
    GR: { country: "Greece", phone: "+30" },
    HU: { country: "Hungary", phone: "+36" },
    HR: { country: "Croatia", phone: "+385" },
    SK: { country: "Slovakia", phone: "+421" },
    BG: { country: "Bulgaria", phone: "+359" },
    LT: { country: "Lithuania", phone: "+370" },
    LV: { country: "Latvia", phone: "+371" },
    EE: { country: "Estonia", phone: "+372" },
    SI: { country: "Slovenia", phone: "+386" },
    CL: { country: "Chile", phone: "+56" },
    AR: { country: "Argentina", phone: "+54" },
    CO: { country: "Colombia", phone: "+57" },
    PE: { country: "Peru", phone: "+51" },
    VE: { country: "Venezuela", phone: "+58" },
    SA: { country: "Saudi Arabia", phone: "+966" },
    IL: { country: "Israel", phone: "+972" },
    KE: { country: "Kenya", phone: "+254" },
    GH: { country: "Ghana", phone: "+233" },
    TZ: { country: "Tanzania", phone: "+255" },
    LK: { country: "Sri Lanka", phone: "+94" },
};

const Profile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [successToast, setSuccessToast] = useState({ open: false, message: "" });

    const [formData, setFormData] = useState({
        branch_name: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "+47",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [currentLogo, setCurrentLogo] = useState(null);
    const [originalUserData, setOriginalUserData] = useState(null);
    const [hasPasswordSet, setHasPasswordSet] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(null); // null = not checked, true = available, false = taken
    const [checkingUsername, setCheckingUsername] = useState(false);
    const [usernameCheckTimeout, setUsernameCheckTimeout] = useState(null);
    
    // State for separate phone inputs to avoid interference
    const [phoneParts, setPhoneParts] = useState({
        code: "+47",
        number: ""
    });
    
    // State to track current user data including onboarding_completed
    const [currentUserState, setCurrentUserState] = useState(currentUser);
    const [deletingBranchData, setDeletingBranchData] = useState(false);
    const [runOnboardingTutorial, setRunOnboardingTutorial] = useState(false);

    // Autosave state: tracks per-field saving status ("saving", "saved", "error")
    const [autoSaveStatus, setAutoSaveStatus] = useState({});
    const autoSaveTimers = useRef({});
    const lastSavedValues = useRef({});

    const ALLOWED_TEST_EMAILS = [
        "torkel@multico.no",
        "anna@digifront.biz",
        "anna@fargerikemajorstuen.no",
        "agagatek29@gmail.com",
        "task@digifront.biz"
    ];
    const canDeleteBranchData =
        currentUserState?.email &&
        ALLOWED_TEST_EMAILS.includes(String(currentUserState.email).trim().toLowerCase());
    
    // Helper to split user_name into first/last and combine back
    const splitUserName = (userName) => {
        if (!userName) return { first: "", last: "" };
        const parts = userName.trim().split(" ");
        return { first: parts[0] || "", last: parts.slice(1).join(" ") || "" };
    };
    const combineUserName = (first, last) => [first, last].filter(Boolean).join(" ");

    // Helper function to extract country code from phone number
    const getCountryCode = (phone) => {
        if (!phone || typeof phone !== 'string') {
            return '+47'; // Default to Norway
        }
        
        // If it contains a space, split by space (new format)
        if (phone.includes(' ')) {
            const parts = phone.split(' ');
            return parts[0] || '+47';
        }
        
        // Legacy format handling (no space)
        
        // If it doesn't start with +, return default
        if (!phone.startsWith('+')) {
            return '+47';
        }
        
        // Extract all digits immediately after + (country code can be any length)
        // Note: This is imperfect for legacy data without spaces, as it grabs all digits.
        // But for new data with spaces, the check above handles it.
        const match = phone.match(/^\+(\d+)/);
        if (match && match[1]) {
            return '+' + match[1];
        }
        
        // If we have just '+', return it (allow user to type)
        if (phone === '+' || (phone.startsWith('+') && phone.length === 1)) {
            return '+';
        }
        
        return '+47';
    };

    // Helper function to extract phone number without country code
    const getPhoneNumber = (phone) => {
        if (!phone || typeof phone !== 'string') {
            return '';
        }
        
        // If it contains a space, split by space (new format)
        if (phone.includes(' ')) {
            const parts = phone.split(' ');
            return parts[1] || '';
        }
        
        // Legacy format handling
        
        // If it doesn't start with +, return the whole thing (might be just digits)
        if (!phone.startsWith('+')) {
            // If it's all digits, return them
            if (/^\d+$/.test(phone)) {
                return phone;
            }
            return '';
        }
        
        // Remove country code - find where digits end after +
        const countryCodeMatch = phone.match(/^\+\d+/);
        if (countryCodeMatch) {
            const phoneNumber = phone.substring(countryCodeMatch[0].length);
            return phoneNumber;
        }
        
        return '';
    };
    
    // Check if user is a free trial user (using currentUserState)
    const isFreeTrialUser = currentUserState?.is_free_trial === true;
    
    // Check which fields need to be updated (for free trial onboarding)
    // For password: check if user has set one in this session
    const needsPassword = isFreeTrialUser && !hasPasswordSet && !passwordChanged;
    const needsLogo = isFreeTrialUser && !currentLogo && !logoPreview;
    const needsPhone = isFreeTrialUser && (!phoneParts.number || phoneParts.number.length === 0);
    
    // Check if profile is complete
    const isProfileComplete = !needsLogo && !needsPhone;
    
    // Check if user has visited live queue page before
    const [hasVisitedLiveQueue, setHasVisitedLiveQueue] = useState(() => {
        return localStorage.getItem('live_queue_visited') === 'true';
    });

    // Check if onboarding is completed
    const isOnboardingCompleted = currentUserState?.onboarding_completed === 1;
    
    // Show onboarding instructions only if onboarding is not completed and user hasn't visited live queue
    const shouldShowOnboardingInstructions = !isOnboardingCompleted && !hasVisitedLiveQueue;

    useEffect(() => {
        fetchProfile();
        
        // Cleanup timeouts on unmount
        return () => {
            if (usernameCheckTimeout) {
                clearTimeout(usernameCheckTimeout);
            }
            Object.values(autoSaveTimers.current).forEach(clearTimeout);
        };
    }, []);

    // Update hasVisitedLiveQueue and currentUserState when location changes
    useEffect(() => {
        setHasVisitedLiveQueue(localStorage.getItem('live_queue_visited') === 'true');
        const storedUser = getCurrentUser();
        if (storedUser) {
            setCurrentUserState(storedUser);
        }
    }, [location.pathname]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            setError("");
            
            // First try to get from API
            const response = await getProfile(branchId);
            
            if (response.success) {
                const { first, last } = splitUserName(response.user.user_name || "");
                const userData = {
                    branch_name: response.user.branch_name || "",
                    first_name: first,
                    last_name: last,
                    email: response.user.email || "",
                    phone: response.user.phone || "+47",
                    password: "", // Don't load password
                };
                setFormData(userData);
                setOriginalUserData(userData);
                lastSavedValues.current = {
                    branch_name: userData.branch_name,
                    user_name: response.user.user_name || "",
                    email: userData.email,
                    phone: response.user.phone || "+47",
                };

                // Initialize phone parts
                const phone = response.user.phone || "+47";
                setPhoneParts({
                    code: getCountryCode(phone),
                    number: getPhoneNumber(phone)
                });
                
                // Update localStorage with onboarding_completed if available
                if (response.user.onboarding_completed !== undefined) {
                    const updatedUser = {
                        ...currentUserState,
                        onboarding_completed: response.user.onboarding_completed,
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    setCurrentUserState(updatedUser);
                }
                
                // For free trial users, we'll require password to be set during onboarding
                // We'll track if user has set a password in this session
                if (isFreeTrialUser) {
                    // Check localStorage to see if password was set in this session
                    const passwordSet = localStorage.getItem('free_trial_password_set');
                    setHasPasswordSet(passwordSet === 'true');
                }
                
                // Set current logo if exists
                if (response.user.logo) {
                    const logoUrl = getStorageUrl(`/storage/logos/${response.user.logo}`);
                    setCurrentLogo(logoUrl);
                } else {
                    setCurrentLogo(null);
                }
            } else {
                // Fallback to localStorage if API fails
                if (currentUserState) {
                    const { first: f1, last: l1 } = splitUserName(currentUserState.user_name || "");
                    setFormData({
                        branch_name: currentUserState.branch_name || "",
                        first_name: f1,
                        last_name: l1,
                        email: currentUserState.email || "",
                        phone: currentUserState.phone || "+47",
                        password: "",
                    });

                    // Initialize phone parts
                    const phone = currentUserState.phone || "+47";
                    setPhoneParts({
                        code: getCountryCode(phone),
                        number: getPhoneNumber(phone)
                    });

                    // Set current logo from localStorage if exists
                    if (currentUserState.logo) {
                        const logoUrl = getStorageUrl(`/storage/logos/${currentUserState.logo}`);
                        setCurrentLogo(logoUrl);
                    } else {
                        setCurrentLogo(null);
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching profile:", err);

            // Fallback to localStorage
            if (currentUserState) {
                const { first: f2, last: l2 } = splitUserName(currentUserState.user_name || "");
                setFormData({
                    branch_name: currentUserState.branch_name || "",
                    first_name: f2,
                    last_name: l2,
                    email: currentUserState.email || "",
                    phone: currentUserState.phone || "+47",
                    password: "",
                });

                // Initialize phone parts from cached data
                const phone = currentUserState.phone || "+47";
                setPhoneParts({
                    code: getCountryCode(phone),
                    number: getPhoneNumber(phone)
                });

                // Set current logo from localStorage if exists
                if (currentUserState.logo) {
                    const logoUrl = getStorageUrl(`/storage/logos/${currentUserState.logo}`);
                    setCurrentLogo(logoUrl);
                } else {
                    setCurrentLogo(null);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBranchData = async () => {
        const confirmed = await Swal.fire({
            title: "Delete branch and all data?",
            text: "This will delete the branch and all related data (queue, services, menu, ads, tutorial). You will be logged out. Only for testing.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete and logout",
        });
        if (!confirmed.isConfirmed) return;
        setDeletingBranchData(true);
        setError("");
        try {
            const res = await deleteBranchDataForTesting(branchId);
            if (res?.success) {
                if (res?.require_logout) {
                    clearAuthData();
                    window.location.href = "/";
                    return;
                }
                setSuccessToast({ open: true, message: res.message || "Branch data deleted." });
            } else {
                setError(res?.message || "Delete failed.");
            }
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Failed to delete branch data.");
        } finally {
            setDeletingBranchData(false);
        }
    };

    const checkUsername = async (username) => {
        const originalCombined = combineUserName(originalUserData?.first_name, originalUserData?.last_name);
        if (!username || username === originalCombined) {
            // Don't check if empty or unchanged
            setUsernameAvailable(null);
            return;
        }

        try {
            setCheckingUsername(true);
            const response = await checkUsernameAvailability(username, branchId);
            setUsernameAvailable(response.available);
        } catch (error) {
            console.error("Error checking username:", error);
            setUsernameAvailable(null);
        } finally {
            setCheckingUsername(false);
        }
    };

    // Autosave a single field on blur
    const autoSaveField = useCallback(async (fieldName, fieldValue) => {
        // Skip if value hasn't changed from last saved
        if (lastSavedValues.current[fieldName] === fieldValue) return;

        // Don't autosave password (requires explicit submit)
        if (fieldName === "password") return;

        // Name fields are handled by autoSaveUserName
        if (fieldName === "first_name" || fieldName === "last_name") return;

        if (!branchId) return;

        // Clear any existing timer for this field
        if (autoSaveTimers.current[fieldName]) {
            clearTimeout(autoSaveTimers.current[fieldName]);
        }

        setAutoSaveStatus(prev => ({ ...prev, [fieldName]: "saving" }));

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("branch_id", String(branchId));
            formDataToSend.append(fieldName, fieldValue);

            const response = await updateProfile(formDataToSend);

            if (response.success) {
                lastSavedValues.current[fieldName] = fieldValue;
                setAutoSaveStatus(prev => ({ ...prev, [fieldName]: "saved" }));

                // Update localStorage with new data
                const updatedUser = {
                    ...currentUserState,
                    ...response.user,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setCurrentUserState(updatedUser);
                if (fieldName === "branch_name") {
                    localStorage.setItem("branch_name", fieldValue);
                }

                // Clear "saved" indicator after 2 seconds
                autoSaveTimers.current[fieldName] = setTimeout(() => {
                    setAutoSaveStatus(prev => ({ ...prev, [fieldName]: null }));
                }, 2000);
            } else {
                setAutoSaveStatus(prev => ({ ...prev, [fieldName]: "error" }));
            }
        } catch (err) {
            console.error(`Autosave error for ${fieldName}:`, err);
            setAutoSaveStatus(prev => ({ ...prev, [fieldName]: "error" }));
        }
    }, [branchId, currentUserState]);

    // Autosave combined user_name when first_name or last_name blurs
    const autoSaveUserName = useCallback(async () => {
        const combined = combineUserName(formData.first_name, formData.last_name);

        if (lastSavedValues.current.user_name === combined) return;
        if (usernameAvailable === false) return;
        if (!branchId) return;

        if (autoSaveTimers.current.user_name) {
            clearTimeout(autoSaveTimers.current.user_name);
        }

        setAutoSaveStatus(prev => ({ ...prev, user_name: "saving" }));

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("branch_id", String(branchId));
            formDataToSend.append("user_name", combined);

            const response = await updateProfile(formDataToSend);

            if (response.success) {
                lastSavedValues.current.user_name = combined;
                setAutoSaveStatus(prev => ({ ...prev, user_name: "saved" }));

                const updatedUser = {
                    ...currentUserState,
                    ...response.user,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setCurrentUserState(updatedUser);

                autoSaveTimers.current.user_name = setTimeout(() => {
                    setAutoSaveStatus(prev => ({ ...prev, user_name: null }));
                }, 2000);
            } else {
                setAutoSaveStatus(prev => ({ ...prev, user_name: "error" }));
            }
        } catch (err) {
            console.error("Autosave error for user_name:", err);
            setAutoSaveStatus(prev => ({ ...prev, user_name: "error" }));
        }
    }, [branchId, formData.first_name, formData.last_name, usernameAvailable, currentUserState]);

    // Autosave phone on blur (combines code + number)
    const autoSavePhone = useCallback(async () => {
        const currentCode = phoneParts.code || "+47";
        const currentNumber = phoneParts.number;
        const phoneValue = currentNumber ? currentCode + " " + currentNumber : "";

        if (lastSavedValues.current.phone === phoneValue) return;
        if (currentNumber && currentNumber.length < 4) return;
        if (!branchId) return;

        setAutoSaveStatus(prev => ({ ...prev, phone: "saving" }));

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("branch_id", String(branchId));
            formDataToSend.append("phone", phoneValue);

            const response = await updateProfile(formDataToSend);

            if (response.success) {
                lastSavedValues.current.phone = phoneValue;
                setAutoSaveStatus(prev => ({ ...prev, phone: "saved" }));

                const updatedUser = {
                    ...currentUserState,
                    ...response.user,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setCurrentUserState(updatedUser);

                if (autoSaveTimers.current.phone) clearTimeout(autoSaveTimers.current.phone);
                autoSaveTimers.current.phone = setTimeout(() => {
                    setAutoSaveStatus(prev => ({ ...prev, phone: null }));
                }, 2000);
            } else {
                setAutoSaveStatus(prev => ({ ...prev, phone: "error" }));
            }
        } catch (err) {
            console.error("Autosave error for phone:", err);
            setAutoSaveStatus(prev => ({ ...prev, phone: "error" }));
        }
    }, [branchId, phoneParts, currentUserState]);

    // Helper to render autosave indicator for a field
    const renderAutoSaveIndicator = (fieldName) => {
        const status = autoSaveStatus[fieldName];
        if (!status) return null;
        return (
            <Fade in={!!status}>
                <Typography
                    variant="caption"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        color: status === "saving" ? "text.secondary"
                            : status === "saved" ? "success.main"
                            : "error.main",
                    }}
                >
                    {status === "saving" && <><CircularProgress size={10} /> Saving...</>}
                    {status === "saved" && <><CheckCircle sx={{ fontSize: 14 }} /> Saved</>}
                    {status === "error" && <><Warning sx={{ fontSize: 14 }} /> Save failed</>}
                </Typography>
            </Fade>
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const updated = { ...prev, [name]: value };

            // Check username availability when first/last name changes
            if (name === "first_name" || name === "last_name") {
                const combined = combineUserName(updated.first_name, updated.last_name);
                if (usernameCheckTimeout) clearTimeout(usernameCheckTimeout);
                setUsernameAvailable(null);
                const timeout = setTimeout(() => checkUsername(combined), 500);
                setUsernameCheckTimeout(timeout);
            }

            return updated;
        });

        if (name === "password") {
            setPasswordChanged(true);
        }
    };

    // Handle country code change
    const handleCountryCodeChange = (e) => {
        const inputValue = e.target.value;
        
        // Only allow + and digits, remove other characters
        let newCode = inputValue.replace(/[^+\d]/g, '');
        
        // If completely empty, allow it (but sync as empty code)
        if (!newCode || newCode.trim() === '') {
            setPhoneParts(prev => ({ ...prev, code: '' }));
            setFormData(prev => ({ ...prev, phone: '' + phoneParts.number }));
            return;
        }
        
        // If user has just '+', allow it
        if (newCode === '+') {
            setPhoneParts(prev => ({ ...prev, code: '+' }));
            setFormData(prev => ({ ...prev, phone: '+' + phoneParts.number }));
            return;
        }
        
        // Ensure it starts with +
        if (!newCode.startsWith('+')) {
            newCode = '+' + newCode;
        } else {
            // Remove any extra + signs (keep only the first one)
            const parts = newCode.split('+');
            newCode = '+' + parts.filter(p => p).join('');
        }
        
        // Update state
        setPhoneParts(prev => ({ ...prev, code: newCode }));
        setFormData(prev => ({ ...prev, phone: newCode + ' ' + phoneParts.number }));
    };

    // Handle phone number change (without country code)
    const handlePhoneNumberChange = (e) => {
        const inputValue = e.target.value;
        
        // Only allow digits in phone number part
        const newNumber = inputValue.replace(/\D/g, '');
        
        // Use current code or default to +47 if empty (for the combined string)
        const currentCode = phoneParts.code || '+47';
        
        // Update state
        setPhoneParts(prev => ({ ...prev, number: newNumber }));
        setFormData(prev => ({ ...prev, phone: currentCode + ' ' + newNumber }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError("Please select an image file");
                return;
            }
            
            // Validate file size (2MB max)
            if (file.size > 2 * 1024 * 1024) {
                setError("Image size must be less than 2MB");
                return;
            }
            
            setLogoFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        setCurrentLogo(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccessToast({ open: false, message: "" });

        // Check if username is available before submitting
        if (usernameAvailable === false) {
            setError("Please choose a different username. This one is already taken.");
            return;
        }

        setSaving(true);

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();
            
            // Ensure branchId is valid
            if (!branchId) {
                setError("Branch ID is missing. Please login again.");
                setSaving(false);
                return;
            }
            
            formDataToSend.append('branch_id', String(branchId)); // Ensure it's a string
            formDataToSend.append('branch_name', formData.branch_name || '');
            formDataToSend.append('user_name', combineUserName(formData.first_name, formData.last_name));
            formDataToSend.append('email', formData.email || '');
            
            // Validate phone number format (must include country code)
            if (phoneParts.number && phoneParts.number.trim()) {
                const countryCode = phoneParts.code;
                const phoneNumber = phoneParts.number;
                
                if (!countryCode || !countryCode.startsWith('+')) {
                    setError("Country code must start with +");
                    setSaving(false);
                    return;
                }
                
                if (!phoneNumber || phoneNumber.length < 4) {
                    setError("Phone number must be at least 4 digits");
                    setSaving(false);
                    return;
                }
                
                formDataToSend.append('phone', countryCode + ' ' + phoneNumber);
            } else {
                formDataToSend.append('phone', '');
            }

            // Only include password if it was changed
            if (passwordChanged && formData.password) {
                if (formData.password.length < 6) {
                    setError("Password must be at least 6 characters long");
                    setSaving(false);
                    return;
                }
                formDataToSend.append('password', formData.password);
            }

            // Add logo file if selected
            if (logoFile) {
                formDataToSend.append('logo', logoFile);
            }

            const response = await updateProfile(formDataToSend);

            if (response.success) {
                // Update localStorage with new user data
                const updatedUser = {
                    ...currentUserState,
                    branch_name: response.user.branch_name,
                    user_name: response.user.user_name,
                    email: response.user.email,
                    phone: response.user.phone,
                    logo: response.user.logo,
                    onboarding_completed: response.user.onboarding_completed,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setCurrentUserState(updatedUser);
                localStorage.setItem("branch_name", response.user.branch_name);

                // Update logo preview if logo was updated
                if (response.user.logo) {
                    const logoUrl = getStorageUrl(`/storage/logos/${response.user.logo}`);
                    setCurrentLogo(logoUrl);
                }

                setSuccessToast({ open: true, message: "Profile updated successfully!" });
                
                // Update hasPasswordSet if password was changed
                if (passwordChanged && formData.password) {
                    setHasPasswordSet(true);
                    localStorage.setItem('free_trial_password_set', 'true');
                }
                
                setPasswordChanged(false);
                setLogoFile(null);
                setLogoPreview(null);
                setFormData((prev) => ({ ...prev, password: "" }));

                // Check if profile is now complete (for free trial onboarding)
                const updatedHasPassword = passwordChanged && formData.password ? true : hasPasswordSet;
                const updatedNeedsPassword = isFreeTrialUser && !updatedHasPassword;
                const updatedNeedsLogo = isFreeTrialUser && !response.user.logo && !logoFile;
                const updatedNeedsPhone = isFreeTrialUser && !response.user.phone;
                const profileNowComplete = !updatedNeedsPassword && !updatedNeedsLogo && !updatedNeedsPhone;

                Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: profileNowComplete && isFreeTrialUser
                        ? "Profile updated! Let's continue with QR code setup."
                        : "Your profile has been updated successfully.",
                    confirmButtonColor: "#28a745",
                    confirmButtonText: "OK",
                }).then(() => {
                    // If profile is complete and user is free trial, redirect to QR page
                    if (profileNowComplete && isFreeTrialUser) {
                        navigate("/home/qr");
                    }
                });
            } else {
                setError(response.message || "Failed to update profile");
            }
        } catch (err) {
            console.error("Error updating profile:", err);
            setError(
                err.response?.data?.message ||
                    "An error occurred while updating your profile. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleContinueToQR = async () => {
        // Check if username is available before submitting
        if (usernameAvailable === false) {
            setError("Please choose a different username. This one is already taken.");
            return;
        }

        // Check if username is still being validated
        if (checkingUsername) {
            setError("Please wait while we check username availability.");
            return;
        }

        setError("");
        setSuccessToast({ open: false, message: "" });
        setSaving(true);

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();
            
            // Ensure branchId is valid
            if (!branchId) {
                setError("Branch ID is missing. Please login again.");
                setSaving(false);
                return;
            }
            
            formDataToSend.append('branch_id', String(branchId));
            formDataToSend.append('branch_name', formData.branch_name || '');
            formDataToSend.append('user_name', combineUserName(formData.first_name, formData.last_name));
            formDataToSend.append('email', formData.email || '');
            
            // Validate phone number format
            if (phoneParts.number && phoneParts.number.trim()) {
                const countryCode = phoneParts.code;
                const phoneNumber = phoneParts.number;
                
                if (!countryCode || !countryCode.startsWith('+')) {
                    setError("Country code must start with +");
                    setSaving(false);
                    return;
                }
                
                if (!phoneNumber || phoneNumber.length < 4) {
                    setError("Phone number must be at least 4 digits");
                    setSaving(false);
                    return;
                }
                
                formDataToSend.append('phone', countryCode + ' ' + phoneNumber);
            } else {
                formDataToSend.append('phone', '');
            }

            // Only include password if it was changed
            if (passwordChanged && formData.password) {
                if (formData.password.length < 6) {
                    setError("Password must be at least 6 characters long");
                    setSaving(false);
                    return;
                }
                formDataToSend.append('password', formData.password);
            }

            // Add logo file if selected
            if (logoFile) {
                formDataToSend.append('logo', logoFile);
            }

            const response = await updateProfile(formDataToSend);

            if (response.success) {
                // Update localStorage with new user data
                const updatedUser = {
                    ...currentUserState,
                    branch_name: response.user.branch_name,
                    user_name: response.user.user_name,
                    email: response.user.email,
                    phone: response.user.phone,
                    logo: response.user.logo,
                    onboarding_completed: response.user.onboarding_completed,
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                setCurrentUserState(updatedUser);
                localStorage.setItem("branch_name", response.user.branch_name);

                // Update logo preview if logo was updated
                if (response.user.logo) {
                    const logoUrl = getStorageUrl(`/storage/logos/${response.user.logo}`);
                    setCurrentLogo(logoUrl);
                }

                // Update hasPasswordSet if password was changed
                if (passwordChanged && formData.password) {
                    setHasPasswordSet(true);
                    localStorage.setItem('free_trial_password_set', 'true');
                }
                
                setPasswordChanged(false);
                setLogoFile(null);
                setLogoPreview(null);
                setFormData((prev) => ({ ...prev, password: "" }));

                // Navigate to QR setup after successful save
                navigate("/home/qr");
            } else {
                setError(response.message || "Failed to update profile");
                setSaving(false);
            }
        } catch (err) {
            console.error("Error updating profile:", err);
            setError(
                err.response?.data?.message ||
                    "An error occurred while updating your profile. Please try again."
            );
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "50vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Profile Settings
                </Typography>
                <IconButton
                    onClick={() => setRunOnboardingTutorial(true)}
                    title="Show Tutorial"
                    color="primary"
                >
                    <HelpIcon />
                </IconButton>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            <Snackbar
                open={successToast.open}
                autoHideDuration={3000}
                onClose={() => setSuccessToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSuccessToast((t) => ({ ...t, open: false }))}
                    severity="success"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {successToast.message}
                </Alert>
            </Snackbar>

            <Card>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Company Name/Location Name"
                                    name="branch_name"
                                    value={formData.branch_name}
                                    onChange={handleChange}
                                    onBlur={() => autoSaveField("branch_name", formData.branch_name)}
                                    required
                                    variant="outlined"
                                    inputProps={{ "data-onboarding-id": "onboarding-profile-branch_name" }}
                                />
                                {renderAutoSaveIndicator("branch_name")}
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    onBlur={autoSaveUserName}
                                    required
                                    variant="outlined"
                                    error={usernameAvailable === false}
                                    helperText={
                                        checkingUsername
                                            ? "Checking availability..."
                                            : usernameAvailable === false
                                            ? "This name is already taken"
                                            : usernameAvailable === true
                                            ? "Available"
                                            : ""
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {checkingUsername && <CircularProgress size={20} />}
                                                {!checkingUsername && usernameAvailable === true && (
                                                    <CheckCircle color="success" fontSize="small" />
                                                )}
                                                {!checkingUsername && usernameAvailable === false && (
                                                    <Warning color="error" fontSize="small" />
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {renderAutoSaveIndicator("user_name")}
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    onBlur={autoSaveUserName}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={() => autoSaveField("email", formData.email)}
                                    variant="outlined"
                                />
                                {renderAutoSaveIndicator("email")}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                    <FormControl variant="outlined" sx={{ width: "140px" }}>
                                        <InputLabel>Code</InputLabel>
                                        <Select
                                            value={phoneParts.code}
                                            onChange={(e) => {
                                                const newCode = e.target.value;
                                                setPhoneParts(prev => ({ ...prev, code: newCode }));
                                                setFormData(prev => ({ ...prev, phone: newCode + ' ' + phoneParts.number }));
                                            }}
                                            onBlur={autoSavePhone}
                                            label="Code"
                                        >
                                            {Object.values(COUNTRY_PHONE_PREFIXES)
                                                .sort((a, b) => a.country.localeCompare(b.country))
                                                .map((entry) => (
                                                    <MenuItem key={entry.country} value={entry.phone}>
                                                        {entry.phone} ({entry.country})
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        value={phoneParts.number}
                                        onChange={handlePhoneNumberChange}
                                        onBlur={autoSavePhone}
                                        variant="outlined"
                                        error={needsPhone}
                                        helperText={
                                            needsPhone
                                                ? "Phone number is required for free trial users"
                                                : ""
                                        }
                                        placeholder="12345678"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                                                        {phoneParts.number.length} / 15
                                                    </Typography>
                                                    {isFreeTrialUser && (
                                                        !needsPhone ? (
                                                            <CheckCircle color="success" fontSize="small" />
                                                        ) : (
                                                            <Warning color="warning" fontSize="small" />
                                                        )
                                                    )}
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                                {renderAutoSaveIndicator("phone")}
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ position: "relative" }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                        Logo {isFreeTrialUser && needsLogo && <Warning color="warning" sx={{ verticalAlign: "middle", ml: 1 }} />}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                        <Box sx={{ position: "relative", display: "inline-block" }}>
                                            <Box
                                                component="img"
                                                src={logoPreview || currentLogo || DefaultLogo}
                                                alt="Logo Preview"
                                                sx={{
                                                    display: "block",
                                                    maxWidth: 200,
                                                    maxHeight: 120,
                                                    width: "auto",
                                                    height: "auto",
                                                    objectFit: "contain",
                                                    borderRadius: 1,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    p: 0.5,
                                                    backgroundColor: "background.paper",
                                                }}
                                            />
                                            {isFreeTrialUser && (logoPreview || currentLogo) && (
                                                <CheckCircle 
                                                    color="success" 
                                                    sx={{ 
                                                        position: "absolute", 
                                                        bottom: -5, 
                                                        right: -5,
                                                        backgroundColor: "white",
                                                        borderRadius: "50%"
                                                    }} 
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <input
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                id="logo-upload"
                                                type="file"
                                                onChange={handleLogoChange}
                                            />
                                            <label htmlFor="logo-upload">
                                                <Button
                                                    variant={needsLogo ? "contained" : "outlined"}
                                                    color={needsLogo ? "warning" : "primary"}
                                                    component="span"
                                                    startIcon={<CloudUpload />}
                                                    sx={{ mr: 1 }}
                                                >
                                                    {logoPreview || currentLogo ? "Change Logo" : "Upload Logo"}
                                                </Button>
                                            </label>
                                            {(logoPreview || currentLogo) && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<Delete />}
                                                    onClick={handleRemoveLogo}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color={needsLogo ? "warning.main" : "text.secondary"}>
                                        {needsLogo 
                                            ? "Logo is required for free trial users. Please upload your logo."
                                            : "Upload a logo image (JPEG, PNG, JPG, GIF, SVG). Max size: 2MB"}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ position: "relative" }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        variant="outlined"
                                        helperText="No password needed — you log in via email. Only set one as a backup or if staff don't have access to your email."
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff />
                                                        ) : (
                                                            <Visibility />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {isFreeTrialUser && (
                                        <Box sx={{ position: "absolute", right: 40, top: 8 }}>
                                            {formData.password || passwordChanged ? (
                                                <CheckCircle color="success" />
                                            ) : (
                                                <Warning color="warning" />
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        disabled={saving}
                                        sx={{ minWidth: 120 }}
                                        data-onboarding-id="onboarding-profile-save"
                                    >
                                        {saving ? (
                                            <>
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ mr: 1 }}
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save Changes"
                                        )}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="large"
                                        onClick={() => navigate("/home/panel")}
                                    >
                                        Cancel
                                    </Button>
                                    {canDeleteBranchData && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="large"
                                            startIcon={deletingBranchData ? <CircularProgress size={20} /> : <DeleteForever />}
                                            disabled={deletingBranchData}
                                            onClick={handleDeleteBranchData}
                                            sx={{ ml: 1 }}
                                        >
                                            {deletingBranchData ? "Deleting..." : "Delete branch data (testing)"}
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>

            <OnboardingTutorialOverlay
                page="profile"
                run={runOnboardingTutorial}
                onClose={() => setRunOnboardingTutorial(false)}
            />
        </Box>
    );
};

export default Profile;
